import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
// import AppConfig from "config/AppConfig";
// import StorageHelper from "utilities/StorageHelper";
import httpRequest from "../../services/httpRequest";
import AppUtil from "../../utilities/AppUtil";
import snakecaseKeys from "snakecase-keys";
// import noteProcess from './processNote.json';
// Main URL Endpoint
const _urlPrefix = "/process-note";

export const fetchNoteQuestions = createAsyncThunk(
  "process-note/fetch-questions",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/questions`,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchAddProcessNoteStructured = createAsyncThunk(
  "process-note/add-note-structured",
  async (params, { dispatch }) => {
    const { jobId, clientId, handover, answers } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "POST",
      data: snakecaseKeys({ jobId, clientId, handover, answers }),
      successMessage: "toast.add.200",
    });
    if (!result) return;
    if (jobId) dispatch(fetchProcessNotePage({ jobId, clientId }));
    return result.data;
  }
);

export const fetchAddNoteQuestion = createAsyncThunk(
  "process-note/add-question",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/questions`,
      method: "POST",
      data: snakecaseKeys(params),
      successMessage: "toast.add.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchUpdateNoteQuestion = createAsyncThunk(
  "process-note/update-question",
  async ({ id, ...rest }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/questions/${id}`,
      method: "PUT",
      data: snakecaseKeys(rest),
      successMessage: "toast.update.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchSortNoteQuestions = createAsyncThunk(
  "process-note/sort-questions",
  async (questionIds, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/questions/sort`,
      method: "PUT",
      data: snakecaseKeys({ questionIds }),
      successMessage: "toast.update.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchDeleteNoteQuestion = createAsyncThunk(
  "process-note/delete-question",
  async ({ id }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/questions/${id}`,
      method: "DELETE",
      successMessage: "toast.delete.200",
    });
    if (!result) return;
    return { id };
  }
);

export const fetchProcessNotePage = createAsyncThunk(
  "process-note/get-page",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/page",
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchProcessNoteSummary = createAsyncThunk(
  "process-note/get-summary",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/summary",
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchAddProcessNote = createAsyncThunk(
  "process-note/add-note",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "POST",
      data: snakecaseKeys(params),
    });
    if (!result) {
      return;
    }
    dispatch(fetchProcessNotePage({ jobId: params.jobId, clientId: params.clientId }));
    return result.data;
  }
);

export const fetchUpdateProcessNote = createAsyncThunk(
  "process-note/update-note",
  async (params, { dispatch }) => {
    const request = {
      isIncident: params.isIncident,
      note: params.note
    }
    const result = await httpRequest({
      url: `${_urlPrefix}/${params.id}`,
      method: "PUT",
      data: snakecaseKeys(request),
    });
    if (!result) {
      return;
    }
    dispatch(fetchProcessNotePage(params.queries));
    return result.data;
  }
);

export const fetchDeleteProcessNote = createAsyncThunk(
  "process-note/delete-note",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${params.id}`,
      method: "DELETE"
    });
    if (!result) {
      return;
    }
    dispatch(fetchProcessNotePage(params.queries));
    return result.data;
  }
);

export const fetchParticipantNotesWeek = createAsyncThunk(
  "process-note/participant-week",
  async ({ clientId, weekStart }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/participant/${clientId}/week`,
      method: "GET",
      params: { week_start: weekStart },
    });
    if (!result) return;
    return result.data;
  }
);


export const fetchProcessNoteAnswers = createAsyncThunk(
  "process-note/fetch-answers",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${params.id}/answers`,
      method: "GET"
    });
    if (!result) return;
    return result.data;
  }
);

export const requestNoteResubmission = createAsyncThunk(
  "process-note/request-resubmission",
  async ({ id }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/request-resubmission`,
      method: "POST",
      successMessage: "toast.update.200",
    });
    if (!result) return;
    return result.data;
  }
);

const initialState = {
  isLoading: false,
  content: [],
  pagination: {
    totalElements: 0,
    numberOfElements: 0,
    pageSize: 10,
    pageNumber: 1,
    totalPages: 0,
    page: 1
  },
  participantWeekNotes: [],
  participantWeekLoading: false,
  questions: [],
  questionsLoading: false,
  summary: {
    totalProgressNotes: 0,
    qualityFlagsPerDay: {},
    totalLinkedGoalTrackers: 0,
    totalLinkedWorkers: 0,
  },
  summaryLoading: false,
}

const slice = createSlice({
  name: 'process-note',
  initialState: initialState,
  extraReducers: {
    [fetchProcessNotePage.pending]: (state) => {
      state.isLoading = true;
      return state;
    },
    [fetchProcessNotePage.fulfilled]: (state, action) => {
      const { payload } = action;
      if (payload) {
        state.content = payload.content;
        state.pagination.totalElements = payload.totalElements;
        state.pagination.numberOfElements = payload.numberOfElements;
        state.pagination.totalPages = payload.totalPages;
        state.pagination.page = payload.pageNumber;
        state.pagination = { ...state.pagination };
      }
      state.isLoading = false;
      return state;
    },
    [fetchProcessNotePage.rejected]: (state) => {
      state.isLoading = false;
      return state;
    },
    [fetchProcessNoteSummary.pending]: (state) => {
      state.summaryLoading = true;
      return state;
    },
    [fetchProcessNoteSummary.fulfilled]: (state, action) => {
      state.summaryLoading = false;
      if (action.payload) {
        state.summary = action.payload;
      }
      return state;
    },
    [fetchProcessNoteSummary.rejected]: (state) => {
      state.summaryLoading = false;
      return state;
    },
    [fetchParticipantNotesWeek.pending]: (state) => {
      state.participantWeekLoading = true;
      return state;
    },
    [fetchParticipantNotesWeek.fulfilled]: (state, action) => {
      state.participantWeekLoading = false;
      state.participantWeekNotes = action.payload || [];
      return state;
    },
    [fetchParticipantNotesWeek.rejected]: (state) => {
      state.participantWeekLoading = false;
      return state;
    },
    [fetchNoteQuestions.pending]: (state) => {
      state.questionsLoading = true;
      return state;
    },
    [fetchNoteQuestions.fulfilled]: (state, action) => {
      state.questionsLoading = false;
      state.questions = action.payload || [];
      return state;
    },
    [fetchNoteQuestions.rejected]: (state) => {
      state.questionsLoading = false;
      return state;
    },
    [requestNoteResubmission.fulfilled]: (state, action) => {
      if (action.payload) {
        if (state.content) {
          const contentIdx = state.content.findIndex(n => n.id === action.payload.id);
          if (contentIdx !== -1) {
            state.content[contentIdx].resubmissionRequested = action.payload.resubmissionRequested || false;
            state.content[contentIdx].resubmissionRequestedAt = action.payload.resubmissionRequested_at || null;
          }
        }
      }
      return state;
    },
    [fetchAddNoteQuestion.fulfilled]: (state, action) => {
      if (action.payload) state.questions = [...state.questions, action.payload];
      return state;
    },
    [fetchUpdateNoteQuestion.fulfilled]: (state, action) => {
      if (action.payload) {
        const idx = state.questions.findIndex(q => q.id === action.payload.id);
        if (idx !== -1) state.questions[idx] = action.payload;
        else state.questions = [...state.questions, action.payload];
      }
      return state;
    },
    [fetchDeleteNoteQuestion.fulfilled]: (state, action) => {
      if (action.payload) {
        state.questions = state.questions.filter(q => q.id !== action.payload.id);
      }
      return state;
    },
  }

});

// const { reducer, actions } = slice;
// export const { clearAuthData, saveAuthToken, updateOrganizationName } = actions;
export default slice.reducer;

export const getProcessNotePage = createSelector(
  (state) => state.processNote.content,
  (content) => content
);

export const getIsLoadingProcessNotePage = createSelector(
  (state) => state.processNote.isLoading,
  (isLoading) => isLoading
);

export const getPaginationProcessNotePage = createSelector(
  (state) => state.processNote.pagination,
  (pagination) => pagination
);

export const getParticipantWeekNotes = createSelector(
  (state) => state.processNote.participantWeekNotes,
  (notes) => notes
);

export const getNoteQuestions = createSelector(
  (state) => state.processNote.questions,
  (q) => q
);

export const getNoteQuestionsLoading = createSelector(
  (state) => state.processNote.questionsLoading,
  (l) => l
);

export const getParticipantWeekLoading = createSelector(
  (state) => state.processNote.participantWeekLoading,
  (loading) => loading
);

export const getProcessNoteSummary = createSelector(
  (state) => state.processNote.summary,
  (summary) => summary
);

export const getProcessNoteSummaryLoading = createSelector(
  (state) => state.processNote.summaryLoading,
  (loading) => loading
);